import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ScrollText, Search, Loader2, ShieldCheck, Building2, User, Activity,
  Download, RefreshCw, Filter, ChevronLeft, ChevronRight, Clock,
  LogIn, LogOut, AlertTriangle, Wallet, CreditCard, Users, FileText
} from 'lucide-react';
import { superAdminAPI } from '../../lib/walletApi';
import { toast } from 'sonner';

const ACTION_CONFIG = {
  'auth.login_success': { icon: LogIn, color: 'bg-emerald-50 text-emerald-600', label: 'Login' },
  'auth.login_failed': { icon: AlertTriangle, color: 'bg-red-50 text-red-600', label: 'Failed Login' },
  'auth.otp_sent': { icon: ShieldCheck, color: 'bg-blue-50 text-blue-600', label: 'OTP Sent' },
  'auth.otp_failed': { icon: AlertTriangle, color: 'bg-orange-50 text-orange-600', label: 'Bad OTP' },
  'company.create': { icon: Building2, color: 'bg-emerald-50 text-emerald-600', label: 'Company Created' },
  'company.logo_updated': { icon: Building2, color: 'bg-blue-50 text-blue-600', label: 'Logo Updated' },
  'admin.create': { icon: ShieldCheck, color: 'bg-emerald-50 text-emerald-600', label: 'Admin Created' },
  'admin.delete': { icon: ShieldCheck, color: 'bg-red-50 text-red-600', label: 'Admin Deleted' },
  'user.delete': { icon: User, color: 'bg-red-50 text-red-600', label: 'User Deleted' },
  'wallet.load': { icon: Wallet, color: 'bg-emerald-50 text-emerald-600', label: 'Wallet Loaded' },
  'payroll.create': { icon: CreditCard, color: 'bg-blue-50 text-blue-600', label: 'Payroll Created' },
  'payroll.disburse': { icon: CreditCard, color: 'bg-emerald-50 text-emerald-600', label: 'Payroll Disbursed' },
  'reconciliation.run': { icon: Activity, color: 'bg-violet-50 text-violet-600', label: 'Reconciliation' },
};

const ACTOR_LABELS = { super_admin: 'Super Admin', company_admin: 'Company Admin', employee: 'Employee', unknown: 'Unknown' };

function getActionConfig(action) {
  if (ACTION_CONFIG[action]) return ACTION_CONFIG[action];
  if (action?.startsWith('auth.')) return { icon: LogIn, color: 'bg-blue-50 text-blue-600', label: action };
  if (action?.startsWith('company.')) return { icon: Building2, color: 'bg-emerald-50 text-emerald-600', label: action };
  if (action?.startsWith('wallet.')) return { icon: Wallet, color: 'bg-violet-50 text-violet-600', label: action };
  if (action?.startsWith('payroll.')) return { icon: CreditCard, color: 'bg-pink-50 text-pink-600', label: action };
  return { icon: Activity, color: 'bg-gray-50 text-gray-600', label: action };
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionTypes, setActionTypes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 30 };
      if (actionFilter && actionFilter !== 'all') params.action = actionFilter;
      if (actorFilter && actorFilter !== 'all') params.actor_type = actorFilter;
      if (searchQuery) params.search = searchQuery;

      const data = await superAdminAPI.getEnhancedAuditLogs(params);
      setLogs(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
      if (data.action_types) setActionTypes(data.action_types);
    } catch { toast.error('Failed to load logs'); }
    finally { setLoading(false); }
  }, [page, actionFilter, actorFilter, searchQuery]);

  const loadSummary = async () => {
    try {
      const data = await superAdminAPI.getAuditSummary();
      setSummary(data);
    } catch {}
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (actionFilter && actionFilter !== 'all') params.action = actionFilter;
      const resp = await superAdminAPI.exportAuditLogs(params);
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Audit logs exported');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const summaryStats = [
    { label: 'Total Events', value: summary?.total_events || 0, icon: ScrollText, color: 'text-pink-600 bg-pink-50' },
    { label: 'Last 24h', value: summary?.last_24h || 0, icon: Clock, color: 'text-blue-600 bg-blue-50' },
    { label: 'Admin Actions', value: summary?.by_actor_type?.super_admin || 0, icon: ShieldCheck, color: 'text-violet-600 bg-violet-50' },
    { label: 'User Actions', value: (summary?.by_actor_type?.company_admin || 0) + (summary?.by_actor_type?.employee || 0), icon: Users, color: 'text-emerald-600 bg-emerald-50' },
  ];

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-sm text-gray-500">{total.toLocaleString()} events tracked across the platform</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} className="h-9 border-gray-200" data-testid="refresh-logs-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} className="h-9 border-gray-200" data-testid="export-logs-btn">
            {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {summaryStats.map(s => (
          <Card key={s.label} className="border-gray-200 shadow-sm">
            <CardContent className="p-3.5 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${s.color}`}>
                <s.icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xs text-gray-500">{s.label}</p>
                <p className="text-lg font-bold text-gray-900" data-testid={`audit-stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}>{s.value.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search events..." value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
            className="pl-9 h-9 border-gray-200" data-testid="audit-search" />
        </div>
        <Select value={actionFilter || 'all'} onValueChange={v => { setActionFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[180px] h-9 border-gray-200" data-testid="audit-action-filter">
            <Filter className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {actionTypes.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={actorFilter || 'all'} onValueChange={v => { setActorFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[160px] h-9 border-gray-200" data-testid="audit-actor-filter">
            <SelectValue placeholder="All Actors" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actors</SelectItem>
            <SelectItem value="super_admin">Super Admin</SelectItem>
            <SelectItem value="company_admin">Company Admin</SelectItem>
            <SelectItem value="employee">Employee</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Log List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-pink-400" /></div>
      ) : logs.length === 0 ? (
        <Card className="border-gray-200 border-dashed">
          <CardContent className="py-12 text-center">
            <ScrollText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No audit log events found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1.5">
          {logs.map(l => {
            const cfg = getActionConfig(l.action);
            const Icon = cfg.icon;
            return (
              <Card key={l.id} className="border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`audit-log-${l.id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cfg.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{cfg.label}</span>
                      <span className="text-xs text-gray-400">{ACTOR_LABELS[l.actor_type] || l.actor_type}</span>
                    </div>
                    <p className="text-sm text-gray-800 truncate">{l.description || 'No description'}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-gray-400 whitespace-nowrap">{timeAgo(l.created_at)}</p>
                    {l.ip_address && <p className="text-[10px] text-gray-300 mt-0.5">{l.ip_address}</p>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Page {page} of {totalPages} ({total} events)
          </p>
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-8 border-gray-200" data-testid="audit-prev">
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-8 border-gray-200" data-testid="audit-next">
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
